import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {WorkOrderService} from '../../../../Services/work-order.service';
import {EmailService} from '../../../../Services/email.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {DataService} from '../../../../Services/data.service';
import {ChatService} from '../../../../Services/chat.service';
import {AuthService} from '../../../../Services/auth.service';
import {Modal} from 'bootstrap';

@Component({
  selector: 'app-wo-notifications-tab',
  templateUrl: './wo-notifications-tab.component.html',
  standalone: false,
})
export class WoNotificationsTabComponent implements OnInit, OnChanges {
  @Input() headerId: string | null = null;
  @Input() assignedUserId: string | null = null;
  @Input() techName: string = '';
  @Input() woNumber: string = '';
  @Input() entityName: string = '';
  @Input() description: string = '';
  @Input() requiredByDate: string = '';
  @Input() notifyOnDispatch: boolean = false;
  @Input() notifyOnArrival: boolean = false;
  @Input() notifyOnCompletion: boolean = false;
  @Input() customerNotes: string = '';
  @Input() dispatchNotes: string = '';
  @Input() internalNotes: string = '';
  @Input() customerEmail: string = '';
  @Input() techEmail: string = '';
  @Input() techEmailAutoSent: boolean = false;

  log: any[] = [];
  isLoaded = false;
  customMessage = '';
  customTo = '';
  sending = false;
  sendingTestPush = false;

  notifyModes: { calendar: boolean; email: boolean; chat: boolean } = {calendar: true, email: true, chat: true};
  private modalInstance: Modal | null = null;

  constructor(
    private woService: WorkOrderService,
    private notification: BsToastService,
    private dataService: DataService,
    private chatService: ChatService,
    private authService: AuthService,
    private emailService: EmailService,
  ) {
  }

  // Test button for verifying real push-notification delivery (APNs/FCM) to a device.
  // Hits the API's push test endpoint, which delivers to the device token registered
  // for the currently logged-in user.
  sendTestPush(): void {
    if (this.sendingTestPush) {
      return;
    }
    this.sendingTestPush = true;
    this.dataService.post('notifications/test?useMock=false', {}).subscribe({
      next: () => {
        this.notification.showSuccessToast('Test push notification sent.');
        this.sendingTestPush = false;
      },
      error: () => {
        this.notification.showErrorToast('Failed to send test push notification.');
        this.sendingTestPush = false;
      }
    });
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['headerId'] && !changes['headerId'].firstChange) {
      this.load();
    }
  }

  toLocalDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const dispDate = new Date(value);

    console.log('[WO Notifications] toLocalDate value:', dispDate.toLocaleString());
    return new Date(value);
  }

  load(): void {
    if (!this.headerId) {
      this.isLoaded = true;
      return;
    }
    this.woService.getWorkOrder(this.headerId).subscribe({
      next: (res: any) => {
        this.log = res?.wo_notification_log ?? res?.feed ?? [];
        console.log('[WO Notifications] raw log entries:', this.log);
        if (this.log.length > 0) {
          console.log('[WO Notifications] first entry sample:', this.log[0]);
          console.log('[WO Notifications] sent_date value:', this.log[0].sent_date, '| ActivityDate:', this.log[0].ActivityDate);
        }
        this.isLoaded = true;
      },
      error: () => {
        this.isLoaded = true;
      }
    });
  }

  notifyTech(): void {
    if (!this.headerId) {
      return;
    }
    const woLink = `${window.location.origin}/work-orders/work-orders-form?workorderId=${this.headerId}`;
    const subject = `Work Order ${this.woNumber} — You Have Been Dispatched`;
    const body = `Hello ${this.techName},<br><br>` +
      `You have been dispatched for work order <b>${this.woNumber}</b>.<br><br>` +
      `<b>Description:</b> ${this.description}<br>` +
      `<b>Customer:</b> ${this.entityName}<br>` +
      `<b>Required By:</b> ${this.requiredByDate}<br><br>` +
      (this.dispatchNotes ? `<b>Dispatch Notes:</b> ${this.dispatchNotes}<br><br>` : '') +
      (this.internalNotes ? `<b>Internal Notes:</b> ${this.internalNotes}<br><br>` : '') +
      `<a href="${woLink}">View Work Order</a>`;
    const toEmail = this.techEmail || this.customTo.trim();
    if (toEmail) {
      this.emailService.sendEmail({toEmail, subject, body, isBodyHtml: true}, true);
    }
    this.woService.sendNotification(this.headerId, body, toEmail || undefined, 'tech_notify').subscribe({
      next: () => this.load(),
      error: () => {
      }
    });
  }

  notifyCustomer(eventType: 'dispatched' | 'arrived' | 'completed'): void {
    if (!this.headerId) {
      return;
    }
    if (eventType === 'dispatched' && !this.notifyOnDispatch) {
      return;
    }
    if (eventType === 'arrived' && !this.notifyOnArrival) {
      return;
    }
    if (eventType === 'completed' && !this.notifyOnCompletion) {
      return;
    }
    const label = eventType === 'dispatched' ? 'been dispatched' : eventType === 'arrived' ? 'arrived on site' : 'completed';
    const subject = eventType === 'dispatched'
      ? `Work Order ${this.woNumber} — Technician Dispatched`
      : eventType === 'arrived'
        ? `Work Order ${this.woNumber} — Technician Has Arrived`
        : `Work Order ${this.woNumber} — Work Completed`;
    const notesSection = (eventType === 'completed' && this.customerNotes)
      ? `<strong>Technician Notes:</strong><br>${this.customerNotes}<br><br>`
      : '';
    const arrivalNote = eventType === 'dispatched' ? `<strong>Estimated Arrival:</strong> TBD<br>` : '';
    const message = `Dear ${this.entityName},<br><br>` +
      `We wanted to let you know that a technician has <strong>${label}</strong> for work order <strong>${this.woNumber}</strong>.<br><br>` +
      `<strong>Description:</strong> ${this.description}<br>` +
      `<strong>Required By:</strong> ${this.requiredByDate}<br>` +
      arrivalNote +
      `<br>` +
      notesSection +
      `Thank you for your business.`;
    // Send the actual email via EmailService
    if (this.customerEmail) {
      this.emailService.sendEmail({
        toEmail: this.customerEmail,
        subject,
        body: message,
        isBodyHtml: true,
      }, true);
    }
    // Log the notification via WO service
    this.woService.sendNotification(this.headerId, message, this.customerEmail || undefined, eventType).subscribe({
      next: () => {
        this.notification.showSuccessToast(`Customer notified: ${subject}`);
        this.load();
      },
      error: () => {
        this.notification.showErrorToast('Failed to send customer notification.');
      }
    });
  }

  openNotifyModal(): void {
    if (!this.headerId) {
      this.notification.showWarningToast('Save the work order before notifying.');
      return;
    }
    if (!this.assignedUserId) {
      this.notification.showWarningToast('Please assign a technician before notifying.');
      return;
    }
    const el = document.getElementById('woNotifyTechModal_' + this.headerId);
    if (el) {
      this.modalInstance = new Modal(el);
      this.modalInstance.show();
    }
  }

  async sendNotifications(): Promise<void> {
    this.modalInstance?.hide();
    const results: string[] = [];
    const errors: string[] = [];
    const woLink = `${window.location.origin}/work-orders/work-orders-form?workorderId=${this.headerId}`;

    if (this.notifyModes.calendar) {
      const calendarEvent: any = {
        Id: '',
        Title: `Workorder - ${this.woNumber}`,
        ClassNames: 'bg-primary-subtle',
        AllDay: false,
        Start: this.requiredByDate,
        Attendees: [{UserId: this.assignedUserId, Status: 'Pending'}],
        ExtendedProps: {
          Description: `Description: ${this.description}\n\nCustomer: ${this.entityName}\n\n${woLink}`,
          IsOrganizer: true,
          MyStatus: 'Organizer',
          Attendees: [{UserId: this.assignedUserId, Status: 'Pending'}],
          Category: 'Workorder',
          Priority: 'primary'
        }
      };
      console.log('[WO Notify] POST api/Calendar', calendarEvent);
      await new Promise<void>(resolve => {
        this.dataService.post('Calendar', calendarEvent).subscribe({
          next: () => {
            results.push('Calendar event created');
            this.woService.sendNotification(this.headerId!, `Tech Notify: Added to ${this.techName} calendar.`, undefined, 'tech_notify').subscribe({
              next: resolve,
              error: () => resolve()
            });
          },
          error: () => {
            errors.push('Calendar failed');
            resolve();
          }
        });
      });
    }

    if (this.notifyModes.email && !this.techEmailAutoSent && this.headerId) {
      const emailMessage = `Hello ${this.techName},<br><br>You have been assigned work order <b>${this.woNumber}</b>.<br><br><b>Description:</b> ${this.description}<br><b>Customer:</b> ${this.entityName}<br><b>Required By:</b> ${this.requiredByDate}<br><br><a href="${woLink}">View Work Order</a>`;
      await new Promise<void>(resolve => {
        this.woService.sendNotification(
          this.headerId!,
          emailMessage,
          this.customTo.trim() || undefined,
          'tech_notify'
        ).subscribe({
          next: () => {
            results.push('Email sent');
            resolve();
          },
          error: () => {
            errors.push('Email failed');
            resolve();
          }
        });
      });
    }

    if (this.notifyModes.chat) {
      const currentUser = this.authService.currentUser();
      const senderId = (currentUser?.id ?? currentUser?.Id ?? '').toString();
      if (senderId && this.assignedUserId) {
        const chatBody = `📋 Work Order: *${this.woNumber}*\nCustomer: ${this.entityName}\nDescription: ${this.description}\nRequired By: ${this.requiredByDate}\n🔗 ${woLink}`;
        console.log('[WO Notify] Chat to userId:', this.assignedUserId, 'body:', chatBody);
        await new Promise<void>(resolve => {
          this.chatService.createOrGetThread(senderId, this.assignedUserId!).subscribe({
            next: (thread: any) => {
              this.chatService.sendMessage(thread.id, senderId, chatBody).subscribe({
                next: () => {
                  results.push('Chat message sent');
                  resolve();
                },
                error: () => {
                  errors.push('Chat failed');
                  resolve();
                }
              });
            },
            error: () => {
              errors.push('Chat: could not create thread');
              resolve();
            }
          });
        });
      } else {
        errors.push('Chat: sender user ID not found');
      }
    }

    if (results.length) {
      this.notification.showSuccessToast('✅ ' + results.join(' · '));
    }
    if (errors.length) {
      this.notification.showErrorToast('⚠️ ' + errors.join(' · '));
    }
    this.load();
  }

  sendCustom(): void {
    if (!this.headerId || !this.customMessage.trim()) {
      return;
    }
    this.sending = true;
    const customPayload = {event_type: 'custom', message: this.customMessage, to: this.customTo.trim() || undefined};
    console.log('[WO Notify Custom] POST api/WorkOrder/' + this.headerId + '/notify', customPayload);
    this.woService.sendNotification(this.headerId, this.customMessage, this.customTo.trim() || undefined).subscribe({
      next: () => {
        this.notification.showSuccessToast('Notification sent.');
        this.customMessage = '';
        this.customTo = '';
        this.sending = false;
        this.load();
      },
      error: () => {
        this.notification.showErrorToast('Error sending notification.');
        this.sending = false;
      }
    });
  }
}
